import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "cafelabs-admin-theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function lerPreferenciaSalva(): Theme | null {
  try {
    const salvo = window.localStorage.getItem(STORAGE_KEY);
    return salvo === "light" || salvo === "dark" ? salvo : null;
  } catch {
    // localStorage pode estar indisponivel (modo privado restritivo) — cai
    // no default do sistema, sem quebrar a app.
    return null;
  }
}

function lerPreferenciaDoSistema(): Theme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Tema claro/escuro via atributo `data-theme` na raiz (consumido pelos
 * tokens em styles/tokens.css). Default inicial: preferencia salva pelo
 * usuario (localStorage) ou, na ausencia dela, `prefers-color-scheme` do
 * sistema. A partir do primeiro toggle manual, a escolha do usuario passa a
 * valer sempre (nao volta a seguir o sistema sozinha). */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => lerPreferenciaSalva() ?? lerPreferenciaDoSistema());

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  function setTheme(novo: Theme) {
    setThemeState(novo);
    try {
      window.localStorage.setItem(STORAGE_KEY, novo);
    } catch {
      // Sem persistencia disponivel: o tema ainda funciona nesta sessao.
    }
  }

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme: () => setTheme(theme === "dark" ? "light" : "dark"),
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme precisa ser usado dentro de <ThemeProvider>.");
  }
  return ctx;
}
